import { PagedResults } from "common/models";
import apiClient from "./apiClient";
import {
  AddPointHubResult,
  CreatePointOutcome,
  CreatePointRequest,
  CreatePointResult,
  NearbyPoint,
  NearbyPointsSearch,
  PointFieldErrors,
  PointHubToggleResult,
  PointResult,
  PointsSearch,
} from "points/models";
import { mapCreatePointErrors } from "points/pointValidation";

const getPoint = async (id: number): Promise<PointResult> => {
  return apiClient.get(`/points/${id}`);
};

const search = async (request: PointsSearch): Promise<PagedResults<PointResult>> => {
  return apiClient.post(`/points`, {
    name: request.name,
    clientIds: request.clientIds?.map(c=>c.id),
    hubIds: request.hubIds?.map(m=>m.id),
    sortBy: request.sortName,
    sortAsc: request.sortAsc,
    offset: request.pagedSearchOptions.offset,
    pageSize: request.pagedSearchOptions.pageSize,
    // Tri-state, and `false` is a REAL value here -- it means "inactive only". This payload is
    // built field by field, so anything not named on this list is simply never sent: leaving
    // isActive off made the server see null and return everything, which looked like a working
    // "Active" filter only because most points are active.
    isActive: request.isActive,
    // Sent flat, matching the API's own shape. All four or none -- a half-specified box would
    // filter on one axis and silently show the wrong set.
    minLatitude: request.bounds?.minLatitude,
    maxLatitude: request.bounds?.maxLatitude,
    minLongitude: request.bounds?.minLongitude,
    maxLongitude: request.bounds?.maxLongitude,
  });
};

// Assigns a hub to this point. Distance and DriveTime are left to the server's
// "not yet calculated" placeholder; nothing here computes a distance.
const addHub = async (clientPointId: number, hubId: number): Promise<AddPointHubResult> => {
  return apiClient.post(`/points/${clientPointId}/hubs`, { clientPointId, hubId });
};

// Existing points near a coordinate. Read-only, and asked as soon as the add-a-point screen has
// a coordinate rather than at save time -- the answer is most useful before the user has typed
// the rest of the form, not after.
const getNearby = async (request: NearbyPointsSearch): Promise<NearbyPoint[]> => {
  return apiClient.get(`/points/nearby`, {
    latitude: request.latitude,
    longitude: request.longitude,
    radiusMeters: request.radiusMeters,
    clientId: request.clientId,
    limit: request.limit,
  });
};

/**
 * Adds a client point.
 *
 * Returns an outcome rather than throwing, because two of the four answers are not errors. A 409
 * means the server found points close enough that it wants the user to look at them first; the
 * body carries those points and the screen renders them. A 400 carries per-field validation
 * messages that belong against the inputs that caused them. Only a transport failure or an
 * unrecognised status is reported as an error.
 *
 * The server is the gate. The client-side checks in pointValidation exist to save a round trip,
 * and a save that gets past them can still legitimately come back 400.
 */
const createPoint = async (request: CreatePointRequest): Promise<CreatePointOutcome> => {
  try {
    const point = await apiClient.post<CreatePointRequest, any>(`/points/create`, request);
    return { status: "created", point };
  } catch (e: any) {
    const status = e?.response?.status;
    const data = e?.response?.data;

    if (status === 409) {
      // The BFF forwards OrderApi's 409 body verbatim, and that body is a CreatePointResult --
      // the blocking points are on `nearbyPoints`. Reading the wrong property here is not a
      // cosmetic miss: an empty list means the user is shown nothing to acknowledge and the
      // resubmit carries no acknowledged ids, which is exactly the state that triggers the 409.
      // The save could never complete.
      const nearby: NearbyPoint[] = Array.isArray(data) ? data : data?.nearbyPoints ?? [];
      return { status: "conflict", nearby };
    }

    if (status === 400) {
      return {
        status: "invalid",
        fieldErrors: mapCreatePointErrors(data),
        // OrderApi puts its summary on `message`; a model-binding rejection uses ProblemDetails'
        // title/detail instead.
        message: data?.message || data?.title || data?.detail,
      };
    }

    return {
      status: "error",
      message: data?.title || data?.detail || e?.message || "Could not save the location.",
    };
  }
};

/**
 * What activatePoint reports back.
 *
 * Declared here rather than in points/models because it belongs to this call and nothing else
 * reads it yet. `invalid` keeps the field errors as well as the message: the message is what the
 * panel shows, the errors are the shape the rest of the points code already speaks.
 */
export type ActivatePointOutcome =
  | { status: "activated"; point: CreatePointResult }
  | { status: "invalid"; fieldErrors: PointFieldErrors; message: string }
  | { status: "error"; message: string };

/**
 * Reactivates a deactivated client point.
 *
 * The nearby check on the add-a-point screen counts inactive points deliberately -- a deactivated
 * row is still a duplicate -- which left the operator looking at the location they wanted, greyed
 * out, with the only way forward being a second copy of it. This is the other way forward.
 *
 * Returns an outcome rather than throwing, the same as createPoint, because a refusal is an
 * answer the screen has to render rather than an exception. OrderApi refuses the feed-managed
 * railroads (UP, BNSF) and CSX, and refuses a client id that does not own the row; each refusal
 * comes back 400 with the rule that produced it. The call is idempotent -- reactivating an
 * already-active point succeeds.
 *
 * Both 400 shapes are read, as they are for create: OrderApi's own validator answers with a
 * `CreatePointResult` whose `errors` is a LIST of RuleError, while ASP.NET model binding rejects
 * a malformed request before the controller runs and answers with a ValidationProblemDetails
 * DICTIONARY. mapCreatePointErrors handles both.
 */
const activatePoint = async (clientPointId: number, clientId: number): Promise<ActivatePointOutcome> => {
  try {
    // Client id on the query string, matching the BFF route. There is no body -- the id is the
    // route and the client id is the scope check.
    const point = await apiClient.post<undefined, CreatePointResult>(
      `/points/${clientPointId}/activate?clientId=${clientId}`
    );
    return { status: "activated", point };
  } catch (e: any) {
    const status = e?.response?.status;
    const data = e?.response?.data;

    if (status === 400) {
      const fieldErrors = mapCreatePointErrors(data);
      // The rule messages are preferred over the body's summary here, which is the opposite of
      // createPoint: that screen pins each rule message to the input that caused it and shows the
      // summary above them, whereas this has no form to pin anything to. The rule is the reason
      // -- "BNSF locations are managed by the feed" -- and a ProblemDetails title
      // ("One or more validation errors occurred") is not.
      const ruleMessage = Object.values(fieldErrors).join(" ");
      return {
        status: "invalid",
        fieldErrors,
        message: ruleMessage || data?.message || data?.title || data?.detail || "This location could not be activated.",
      };
    }

    return {
      status: "error",
      message: data?.title || data?.detail || e?.message || "Could not activate this location.",
    };
  }
};

/**
 * What deactivatePoint reports back.
 *
 * Deliberately a separate type from ActivatePointOutcome rather than a shared one with a boolean:
 * the two calls are read at different places on the screen and a `status` that names what
 * happened is what the callers switch on.
 */
export type DeactivatePointOutcome =
  | { status: "deactivated"; point: CreatePointResult }
  | { status: "invalid"; fieldErrors: PointFieldErrors; message: string }
  | { status: "error"; message: string };

/**
 * Retires a client point.
 *
 * The mirror of activatePoint, and refused by OrderApi on the same clients: UP (2) and BNSF (46)
 * send their own locations and CSX (11) is a former client. The screen hides the control for
 * those, but the server is the gate -- this reads the refusal the same way activatePoint does,
 * preferring the rule message over the body's summary because the rule is the reason.
 *
 * Nothing is deleted. The row stays, with its trips and invoices intact, and the point can be
 * activated again from the same screen.
 */
const deactivatePoint = async (clientPointId: number, clientId: number): Promise<DeactivatePointOutcome> => {
  try {
    const point = await apiClient.post<undefined, CreatePointResult>(
      `/points/${clientPointId}/deactivate?clientId=${clientId}`
    );
    return { status: "deactivated", point };
  } catch (e: any) {
    const status = e?.response?.status;
    const data = e?.response?.data;

    if (status === 400) {
      const fieldErrors = mapCreatePointErrors(data);
      const ruleMessage = Object.values(fieldErrors).join(" ");
      return {
        status: "invalid",
        fieldErrors,
        message:
          ruleMessage || data?.message || data?.title || data?.detail || "This location could not be deactivated.",
      };
    }

    return {
      status: "error",
      message: data?.title || data?.detail || e?.message || "Could not deactivate this location.",
    };
  }
};

/** What setPointHubActive reports back. */
export type PointHubToggleOutcome =
  | { status: "toggled"; hub: PointHubToggleResult }
  | { status: "error"; message: string };

/**
 * Turns one point-of-service link on or off -- a single geo.ClientPointToHub row.
 *
 * This is the way out of "already added, but unusable": a point can be active while the link that
 * makes it selectable from a hub is not, which reads on every other screen as the location simply
 * not being there.
 *
 * `clientPointToHubId` is the link's OWN key, not the PointOfServiceID shown in the grid. They are
 * separate columns -- PointOfServiceID is the value trips store in Trip.PickupPointId -- and
 * passing the wrong one would toggle an unrelated link.
 *
 * No client guard here, unlike the point-level calls. Hub links are Railcrew's routing data rather
 * than anything a railroad feed writes, and the case this was built for is a UP point. The client
 * id still goes along so OrderApi can check the link belongs to the point.
 *
 * Returns an outcome rather than throwing, for the same reason the rest of this file does: a
 * refusal is something the grid renders, and OrderApi's message is the only thing that says why.
 */
const setPointHubActive = async (
  clientPointId: number,
  clientPointToHubId: number,
  clientId: number,
  isActive: boolean
): Promise<PointHubToggleOutcome> => {
  const action = isActive ? "activate" : "deactivate";

  try {
    const hub = await apiClient.post<undefined, PointHubToggleResult>(
      `/points/${clientPointId}/hubs/${clientPointToHubId}/${action}?clientId=${clientId}`
    );
    return { status: "toggled", hub };
  } catch (e: any) {
    const data = e?.response?.data;
    // OrderApi answers a refusal with a PointHubToggleResult whose `message` carries the rule --
    // the link is not on this point, or the row is gone. A model-binding rejection never reaches
    // the controller and comes back as ProblemDetails instead.
    const ruleMessage = Array.isArray(data?.errors)
      ? Object.values(mapCreatePointErrors(data)).join(" ")
      : undefined;

    return {
      status: "error",
      message:
        ruleMessage ||
        data?.message ||
        data?.title ||
        data?.detail ||
        e?.message ||
        `Could not ${action} this point of service.`,
    };
  }
};

const pointsService = {
  getPoint,
  search,
  addHub,
  getNearby,
  createPoint,
  activatePoint,
  deactivatePoint,
  setPointHubActive,
};

export default pointsService;
